import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { useRealtimeHub } from './RealtimeContext';
import useRealtimeEvents from '../hooks/useRealtimeEvents';

const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const API = `${normalizeBackendBase(process.env.REACT_APP_BACKEND_URL)}/api`;

/** Safety-net poll while the admin inbox is enabled (WS can miss frames). */
const INBOX_POLL_MS = 45_000;

const AdminNotificationsContext = createContext(null);

const ALERT_EVENTS = new Set([
  'admin_alert_created',
  'skill_request_decided',
  'tournament_updated',
]);

const canUseInbox = (admin) => {
  const role = String(admin?.role || '');
  return role === 'super_admin' || role === 'manager' || role === 'umpire';
};

/** Skill reviews live on Users — umpires only get tournament Events alerts. */
const canReviewSkills = (admin) => {
  const role = String(admin?.role || '');
  return role === 'super_admin' || role === 'manager';
};

const UMPIRE_ALERT_TYPES = new Set([
  'tournament_registration',
  'tournament_payment_pending',
  'tournament_draw_format',
  'tournament_withdrawal',
  'tournament_partner_change',
]);

const alertVisibleToAdmin = (admin, alert) => {
  if (!alert) return false;
  if (canReviewSkills(admin)) return true;
  return UMPIRE_ALERT_TYPES.has(String(alert.type || ''));
};

const clearInboxState = ({
  setAlerts,
  setRegistrationQueue,
  setRegistrationQueueTotal,
  setRegistrationQueuePaidPending,
  setSkillReviewQueue,
  setSkillReviewTotal,
  setWithdrawalQueue,
  setWithdrawalQueueTotal,
  setPartnerChangeQueue,
  setPartnerChangeQueueTotal,
}) => {
  setAlerts([]);
  setRegistrationQueue([]);
  setRegistrationQueueTotal(0);
  setRegistrationQueuePaidPending(0);
  setSkillReviewQueue([]);
  setSkillReviewTotal(0);
  setWithdrawalQueue([]);
  setWithdrawalQueueTotal(0);
  setPartnerChangeQueue([]);
  setPartnerChangeQueueTotal(0);
};

/** Unique each click so opening the same tournament again still retriggers focus. */
export function adminEventsFocusPath(tournamentId) {
  if (!tournamentId) return '/admin/events';
  return `/admin/events?tournament=${encodeURIComponent(tournamentId)}&focus=1&t=${Date.now()}`;
}

export function getAdminAlertPath(alert) {
  if (alert?.type === 'skill_review') {
    return '/admin/users?skillRequest=1';
  }
  if (
    (
      alert?.type === 'tournament_registration'
      || alert?.type === 'tournament_payment_pending'
      || alert?.type === 'tournament_draw_format'
      || alert?.type === 'tournament_withdrawal'
      || alert?.type === 'tournament_partner_change'
    )
    && alert.tournament_id
  ) {
    return adminEventsFocusPath(alert.tournament_id);
  }
  return '/admin/events';
}

export function getRegistrationQueuePath(item) {
  return adminEventsFocusPath(item?.tournament_id);
}

export function getSkillReviewQueuePath(item) {
  if (item?.request_id) {
    return `/admin/users?skillRequest=1&request=${encodeURIComponent(item.request_id)}`;
  }
  return '/admin/users?skillRequest=1';
}

export function getWithdrawalQueuePath(item) {
  return getRegistrationQueuePath(item);
}

export function getPartnerChangeQueuePath(item) {
  return getRegistrationQueuePath(item);
}

export function AdminNotificationsProvider({ children }) {
  const { admin, token } = useAdminAuth();
  const navigate = useNavigate();
  const { connectionGeneration } = useRealtimeHub();
  const [alerts, setAlerts] = useState([]);
  const [registrationQueue, setRegistrationQueue] = useState([]);
  const [registrationQueueTotal, setRegistrationQueueTotal] = useState(0);
  const [registrationQueuePaidPending, setRegistrationQueuePaidPending] = useState(0);
  const [skillReviewQueue, setSkillReviewQueue] = useState([]);
  const [skillReviewTotal, setSkillReviewTotal] = useState(0);
  const [withdrawalQueue, setWithdrawalQueue] = useState([]);
  const [withdrawalQueueTotal, setWithdrawalQueueTotal] = useState(0);
  const [partnerChangeQueue, setPartnerChangeQueue] = useState([]);
  const [partnerChangeQueueTotal, setPartnerChangeQueueTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const toastedIdsRef = useRef(new Set());
  const lastReconcileGenRef = useRef(0);
  const enabled = Boolean(token && canUseInbox(admin));
  // Auth bootstrap: token from localStorage, admin still loading — do not wipe inbox.
  const authBootstrapping = Boolean(token && !admin);

  const fetchRegistrationQueue = useCallback(async () => {
    if (!token) {
      setRegistrationQueue([]);
      setRegistrationQueueTotal(0);
      setRegistrationQueuePaidPending(0);
      return;
    }
    if (authBootstrapping) return;
    if (!canUseInbox(admin)) {
      setRegistrationQueue([]);
      setRegistrationQueueTotal(0);
      setRegistrationQueuePaidPending(0);
      return;
    }
    try {
      const response = await fetch(`${API}/admin/tournaments/registration-queue?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRegistrationQueue(Array.isArray(data?.items) ? data.items : []);
        setRegistrationQueueTotal(Number(data?.total) || 0);
        setRegistrationQueuePaidPending(Number(data?.paid_pending) || 0);
      }
    } catch (error) {
      console.error('Failed to fetch registration queue:', error);
    }
  }, [token, admin, authBootstrapping]);

  const fetchSkillReviewQueue = useCallback(async () => {
    if (!token) {
      setSkillReviewQueue([]);
      setSkillReviewTotal(0);
      return;
    }
    if (authBootstrapping) return;
    if (!canReviewSkills(admin)) {
      setSkillReviewQueue([]);
      setSkillReviewTotal(0);
      return;
    }
    try {
      const response = await fetch(`${API}/admin/skill-change-requests?status=pending&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const requests = Array.isArray(data?.requests) ? data.requests : [];
        setSkillReviewQueue(requests);
        setSkillReviewTotal(Number(data?.pending_total) || requests.length || 0);
      }
    } catch (error) {
      console.error('Failed to fetch skill review queue:', error);
    }
  }, [token, admin, authBootstrapping]);

  const fetchWithdrawalQueue = useCallback(async () => {
    if (!token) {
      setWithdrawalQueue([]);
      setWithdrawalQueueTotal(0);
      return;
    }
    if (authBootstrapping) return;
    if (!canUseInbox(admin)) {
      setWithdrawalQueue([]);
      setWithdrawalQueueTotal(0);
      return;
    }
    try {
      const response = await fetch(`${API}/admin/tournaments/withdrawal-queue?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWithdrawalQueue(Array.isArray(data?.items) ? data.items : []);
        setWithdrawalQueueTotal(Number(data?.total) || 0);
      }
    } catch (error) {
      console.error('Failed to fetch withdrawal queue:', error);
    }
  }, [token, admin, authBootstrapping]);

  const fetchPartnerChangeQueue = useCallback(async () => {
    if (!token) {
      setPartnerChangeQueue([]);
      setPartnerChangeQueueTotal(0);
      return;
    }
    if (authBootstrapping) return;
    if (!canUseInbox(admin)) {
      setPartnerChangeQueue([]);
      setPartnerChangeQueueTotal(0);
      return;
    }
    try {
      const response = await fetch(`${API}/admin/tournaments/partner-change-queue?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPartnerChangeQueue(Array.isArray(data?.items) ? data.items : []);
        setPartnerChangeQueueTotal(Number(data?.total) || 0);
      }
    } catch (error) {
      console.error('Failed to fetch partner-change queue:', error);
    }
  }, [token, admin, authBootstrapping]);

  const fetchAlerts = useCallback(async () => {
    if (!token) {
      clearInboxState({
        setAlerts,
        setRegistrationQueue,
        setRegistrationQueueTotal,
        setRegistrationQueuePaidPending,
        setSkillReviewQueue,
        setSkillReviewTotal,
        setWithdrawalQueue,
        setWithdrawalQueueTotal,
        setPartnerChangeQueue,
        setPartnerChangeQueueTotal,
      });
      return;
    }
    if (authBootstrapping) return;
    if (!canUseInbox(admin)) {
      clearInboxState({
        setAlerts,
        setRegistrationQueue,
        setRegistrationQueueTotal,
        setRegistrationQueuePaidPending,
        setSkillReviewQueue,
        setSkillReviewTotal,
        setWithdrawalQueue,
        setWithdrawalQueueTotal,
        setPartnerChangeQueue,
        setPartnerChangeQueueTotal,
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/alerts?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        setAlerts(list.filter((alert) => alertVisibleToAdmin(admin, alert)));
      }
      await Promise.all([
        fetchRegistrationQueue(),
        fetchSkillReviewQueue(),
        fetchWithdrawalQueue(),
        fetchPartnerChangeQueue(),
      ]);
    } catch (error) {
      console.error('Failed to fetch admin alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [token, admin, authBootstrapping, fetchRegistrationQueue, fetchSkillReviewQueue, fetchWithdrawalQueue, fetchPartnerChangeQueue]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Reconcile after tab sleep / return focus / bfcache restore
  useEffect(() => {
    if (!enabled) return undefined;
    const reconcile = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchAlerts();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') reconcile();
    };
    window.addEventListener('focus', reconcile);
    window.addEventListener('pageshow', reconcile);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', reconcile);
      window.removeEventListener('pageshow', reconcile);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, fetchAlerts]);

  // Catch up after WebSocket reconnect (frames are not replayed)
  useEffect(() => {
    if (!enabled || !connectionGeneration) return;
    if (connectionGeneration === lastReconcileGenRef.current) return;
    lastReconcileGenRef.current = connectionGeneration;
    fetchAlerts();
  }, [enabled, connectionGeneration, fetchAlerts]);

  // Light poll while inbox is enabled
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      fetchAlerts();
    }, INBOX_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, fetchAlerts]);

  const markAsRead = useCallback(async (alertId) => {
    if (!token || !alertId) return;
    try {
      await fetch(`${API}/admin/alerts/${encodeURIComponent(alertId)}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts((prev) =>
        prev.map((a) => (a.alert_id === alertId ? { ...a, read: true } : a)),
      );
    } catch (error) {
      console.error('Failed to mark admin alert read:', error);
    }
  }, [token]);

  const navigateToAlert = useCallback(async (alert) => {
    if (alert?.alert_id && !alert.read) {
      await markAsRead(alert.alert_id);
    }
    navigate(getAdminAlertPath(alert));
  }, [markAsRead, navigate]);

  const navigateToQueueItem = useCallback((item) => {
    navigate(getRegistrationQueuePath(item));
  }, [navigate]);

  const navigateToSkillReview = useCallback((item) => {
    navigate(getSkillReviewQueuePath(item));
  }, [navigate]);

  const navigateToWithdrawalItem = useCallback((item) => {
    navigate(getWithdrawalQueuePath(item));
  }, [navigate]);

  const navigateToPartnerChangeItem = useCallback((item) => {
    navigate(getPartnerChangeQueuePath(item));
  }, [navigate]);

  useRealtimeEvents(
    ALERT_EVENTS,
    useCallback((msg) => {
      if (!enabled) return;
      if (msg.type === 'skill_request_decided' && msg.request_id) {
        if (!canReviewSkills(admin)) return;
        setAlerts((prev) =>
          prev.map((a) => (
            a.type === 'skill_review' && a.request_id === msg.request_id
              ? { ...a, read: true }
              : a
          )),
        );
        setSkillReviewQueue((prev) => prev.filter((r) => r.request_id !== msg.request_id));
        setSkillReviewTotal((n) => Math.max(0, Number(n) - 1));
        fetchSkillReviewQueue();
        return;
      }
      if (msg.type === 'tournament_updated' || msg.type === 'admin_alert_created') {
        fetchRegistrationQueue();
        fetchWithdrawalQueue();
        fetchPartnerChangeQueue();
      }
      if (msg.type === 'admin_alert_created' && msg.alert?.type === 'skill_review') {
        if (canReviewSkills(admin)) fetchSkillReviewQueue();
      }
      if (msg.type !== 'admin_alert_created') return;
      const alert = msg.alert;
      if (!alert?.alert_id) return;
      if (!alertVisibleToAdmin(admin, alert)) return;
      setAlerts((prev) => {
        if (prev.some((x) => x.alert_id === alert.alert_id)) return prev;
        return [alert, ...prev];
      });
      if (!toastedIdsRef.current.has(alert.alert_id)) {
        toastedIdsRef.current.add(alert.alert_id);
        toast.message(alert.title || 'Admin alert', {
          description: alert.message || undefined,
          duration: 5500,
          closeButton: true,
          className: 'cursor-pointer',
          onClick: () => {
            navigateToAlert(alert);
          },
        });
      }
    }, [enabled, admin, navigateToAlert, fetchRegistrationQueue, fetchSkillReviewQueue, fetchWithdrawalQueue, fetchPartnerChangeQueue]),
    { enabled },
  );

  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    try {
      await fetch(`${API}/admin/alerts/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    } catch (error) {
      console.error('Failed to mark all admin alerts read:', error);
    }
  }, [token]);

  const unreadCount = useMemo(
    () => alerts.filter((a) => !a.read).length,
    [alerts],
  );

  const value = useMemo(() => ({
    alerts,
    loading,
    unreadCount,
    registrationQueue,
    registrationQueueTotal,
    registrationQueuePaidPending,
    skillReviewQueue,
    skillReviewTotal,
    withdrawalQueue,
    withdrawalQueueTotal,
    partnerChangeQueue,
    partnerChangeQueueTotal,
    fetchAlerts,
    fetchRegistrationQueue,
    fetchSkillReviewQueue,
    fetchWithdrawalQueue,
    fetchPartnerChangeQueue,
    markAsRead,
    markAllAsRead,
    navigateToAlert,
    navigateToQueueItem,
    navigateToSkillReview,
    navigateToWithdrawalItem,
    navigateToPartnerChangeItem,
    inboxEnabled: enabled,
    skillInboxEnabled: canReviewSkills(admin),
  }), [
    alerts,
    loading,
    unreadCount,
    registrationQueue,
    registrationQueueTotal,
    registrationQueuePaidPending,
    skillReviewQueue,
    skillReviewTotal,
    withdrawalQueue,
    withdrawalQueueTotal,
    partnerChangeQueue,
    partnerChangeQueueTotal,
    fetchAlerts,
    fetchRegistrationQueue,
    fetchSkillReviewQueue,
    fetchWithdrawalQueue,
    fetchPartnerChangeQueue,
    markAsRead,
    markAllAsRead,
    navigateToAlert,
    navigateToQueueItem,
    navigateToSkillReview,
    navigateToWithdrawalItem,
    navigateToPartnerChangeItem,
    enabled,
    admin,
  ]);

  return (
    <AdminNotificationsContext.Provider value={value}>
      {children}
    </AdminNotificationsContext.Provider>
  );
}

export function useAdminNotifications() {
  const ctx = useContext(AdminNotificationsContext);
  if (!ctx) {
    throw new Error('useAdminNotifications must be used within AdminNotificationsProvider');
  }
  return ctx;
}

export default AdminNotificationsContext;
